/* eslint-disable import/no-extraneous-dependencies */
import * as echarts from "echarts";
import PropTypes from "prop-types";
import React, { useCallback, useEffect, useRef } from "react";

import { useAIDashboardStore } from "../context/AIDashboardContext";
import useCoordinatedChartMax from "../hooks/useCoordinatedChartMax";

const CanvLineChart = ({
  title,
  channel,
  "x-field": xField,
  "y-field": yField,
}) => {
  const store = useAIDashboardStore(channel);
  const data = store((state) => state.value);
  const containerRef = useRef(null);
  const chartRef = useRef(null);
  const prevDataRef = useRef(null);

  // Calculate max value from chart data
  const calculateMax = useCallback(() => {
    if (!data || !yField) {
      return 0;
    }

    let normalizedData = Array.isArray(data) ? data : [data];
    normalizedData = normalizedData.map((g) => ({
      ...g,
      size: g?.memberCount || 0,
      hierarchyLevel: g?.hierarchyIds?.length || 0,
      objectives: g?.objectiveIds?.length || 0,
    }));

    const values = normalizedData.map((v) => v[yField] || 0);
    return Math.max(...values, 0);
  }, [data, yField]);

  // Use the coordinated chart max hook to sync Y-axis across multiple charts
  // Coordinate based on x-field and y-field - charts comparing the same data
  // will automatically share the same Y-axis scale
  const maxChannelName = `line-chart-${xField}-${yField}-max`;
  const { coordinatedMax } = useCoordinatedChartMax(
    maxChannelName,
    calculateMax
  );

  // Get reset function for the max value store
  const maxStore = useAIDashboardStore(maxChannelName);
  const resetMax = maxStore((state) => state.publish);

  useEffect(() => {
    let currentDataLength = 0;
    if (Array.isArray(data)) {
      currentDataLength = data.length;
    } else if (data) {
      currentDataLength = 1;
    }

    let prevDataLength = 0;
    if (Array.isArray(prevDataRef.current)) {
      prevDataLength = prevDataRef.current.length;
    } else if (prevDataRef.current) {
      prevDataLength = 1;
    }

    const dataChanged = data !== prevDataRef.current;

    // Reset coordinated max when:
    // 1. Data becomes empty (selection cleared)
    // 2. Data reference changes and we're going from non-empty to non-empty
    //    (this indicates a new selection was made)
    if (!data || currentDataLength === 0) {
      resetMax(null);
    } else if (dataChanged && prevDataLength > 0 && currentDataLength > 0) {
      resetMax(null);
    }

    prevDataRef.current = data;
  }, [data, resetMax]);

  useEffect(() => {
    if (!containerRef.current) {
      return undefined;
    }

    // Initialize chart
    const chart = echarts.init(containerRef.current);
    chartRef.current = chart;

    const handleResize = () => {
      chart.resize();
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
      chart.dispose();
    };
  }, []);

  useEffect(() => {
    if (!chartRef.current || !data) {
      return;
    }

    let normalizedData = data;
    if (normalizedData) {
      normalizedData = Array.isArray(normalizedData)
        ? normalizedData
        : [normalizedData];
    }

    const option = {
      title: {
        text: title,
        left: "center",
      },
      tooltip: {
        trigger: "axis",
      },
      xAxis: {
        type: "category",
        data:
          normalizedData && xField && yField
            ? normalizedData.map((v) => v[xField])
            : [],
        axisLabel: {
          interval: 0,
          rotate: 45,
        },
      },
      yAxis: {
        type: "value",
        name: "Value",
        max: coordinatedMax > 0 ? coordinatedMax : undefined,
      },
      series: [
        {
          data:
            normalizedData && xField && yField
              ? normalizedData.map((v) => v[yField])
              : [],
          type: "line",
          itemStyle: {
            color: "#5b8ff9",
          },
          lineStyle: {
            color: "#5b8ff9",
          },
        },
      ],
    };

    chartRef.current.setOption(option);
  }, [title, data, xField, yField, coordinatedMax]);

  return (
    <div style={{ display: "flex", justifyContent: "center" }}>
      <div style={{ width: "100%", height: "300px" }} ref={containerRef} />
    </div>
  );
};

CanvLineChart.propTypes = {
  title: PropTypes.string.isRequired,
  channel: PropTypes.string.isRequired,
  "x-field": PropTypes.string.isRequired,
  "y-field": PropTypes.string.isRequired,
};

export default CanvLineChart;
